<template>
    <div class="date-range-picker-wrapper">
        <el-date-picker
            v-model="dateRange"
            type="datetimerange"
            :shortcuts="shortcuts"
            range-separator="to"
            start-placeholder="Start date"
            end-placeholder="End date"
            :default-time="defaultTime"
            @change="handleChange"
            size="default"
            style="width: 100%;"
        />
    </div>
</template>

<script>
export default {
    name: 'DateRangePicker',

    props: {
        modelValue: {
            type: Array,
            default: () => null
        },
        defaultRange: {
            type: String,
            default: '24h'
        }
    },

    data() {
        return {
            dateRange: null,
            defaultTime: [
                new Date(2000, 1, 1, 0, 0, 0),
                new Date(2000, 1, 1, 23, 59, 59),
            ],
            shortcuts: [
                {
                    text: '1 Hour',
                    value: () => {
                        // End at start of previous hour, start 1 hour before that
                        const end = new Date(this.startOfHour(new Date()).getTime() - 3600 * 1000);
                        const start = new Date(end.getTime() - 3600 * 1000);
                        return [start, end];
                    },
                    metadata: { range: '1h', interval: '1m' }
                },
                {
                    text: '12 Hours',
                    value: () => {
                        const end = new Date(this.startOfHour(new Date()).getTime() - 3600 * 1000);
                        const start = new Date(end.getTime() - 3600 * 1000 * 12);
                        return [start, end];
                    },
                    metadata: { range: '12h', interval: '1h' }
                },
                {
                    text: '24 Hours',
                    value: () => {
                        const end = new Date(this.startOfHour(new Date()).getTime() - 3600 * 1000);
                        const start = new Date(end.getTime() - 3600 * 1000 * 24);
                        return [start, end];
                    },
                    metadata: { range: '24h', interval: '1h' }
                },
                {
                    text: '2 Days',
                    value: () => {
                        const end = new Date(this.startOfHour(new Date()).getTime() - 3600 * 1000);
                        const start = new Date(end.getTime() - 3600 * 1000 * 24 * 2);
                        return [start, end];
                    },
                    metadata: { range: '2d', interval: '1h' }
                },
                {
                    text: '4 Days',
                    value: () => {
                        const end = new Date(this.startOfHour(new Date()).getTime() - 3600 * 1000);
                        const start = new Date(end.getTime() - 3600 * 1000 * 24 * 4);
                        return [start, end];
                    },
                    metadata: { range: '4d', interval: '1d' }  // Use daily aggregation
                },
                {
                    text: '7 Days',
                    value: () => {
                        const end = new Date(this.startOfHour(new Date()).getTime() - 3600 * 1000);
                        const start = new Date(end.getTime() - 3600 * 1000 * 24 * 7);
                        return [start, end];
                    },
                    metadata: { range: '7d', interval: '1d' }  // Use daily aggregation
                },
                {
                    text: '14 Days',
                    value: () => {
                        const end = new Date(this.startOfHour(new Date()).getTime() - 3600 * 1000);
                        const start = new Date(end.getTime() - 3600 * 1000 * 24 * 14);
                        return [start, end];
                    },
                    metadata: { range: '14d', interval: '1d' }
                },
                {
                    text: '30 Days',
                    value: () => {
                        const end = new Date(this.startOfHour(new Date()).getTime() - 3600 * 1000);
                        const start = new Date(end.getTime() - 3600 * 1000 * 24 * 30);
                        return [start, end];
                    },
                    metadata: { range: '30d', interval: '1d' }
                }
            ]
        };
    },

    watch: {
        modelValue: {
            handler(newVal) {
                if (newVal) {
                    this.dateRange = newVal;
                }
            },
            immediate: true
        }
    },

    mounted() {
        // Set default range on mount if no value provided
        if (!this.dateRange) {
            this.setDefaultRange();
        }
    },

    methods: {
        startOfHour(date) {
            const d = new Date(date);
            d.setMinutes(0, 0, 0);
            return d;
        },

        setDefaultRange() {
            // Find the matching shortcut for default range
            const shortcut = this.shortcuts.find(s => s.metadata.range === this.defaultRange);
            if (shortcut) {
                this.dateRange = shortcut.value();
                this.handleChange(this.dateRange);
            }
        },

        handleChange(value) {
            if (!value) return;

            // Calculate the range and interval based on the time difference
            const [start, end] = value;
            const diffHours = (end - start) / (3600 * 1000);

            let range, interval;

            // Determine range and interval based on time difference
            // Switch to daily aggregation after 2 days to avoid double-counting unique metrics
            if (diffHours <= 1) {
                range = '1h';
                interval = '1m';
            } else if (diffHours <= 12) {
                range = '12h';
                interval = '1h';
            } else if (diffHours <= 24) {
                range = '24h';
                interval = '1h';
            } else if (diffHours <= 48) {
                range = '2d';
                interval = '1h';
            } else if (diffHours <= 96) {
                range = '4d';
                interval = '1d';  // Switch to daily for 3+ days
            } else if (diffHours <= 168) {
                range = '7d';
                interval = '1d';
            } else if (diffHours <= 336) {
                range = '14d';
                interval = '1d';
            } else {
                range = '30d';
                interval = '1d';
            }

            // Emit the change with both the date range and the calculated range/interval
            this.$emit('update:modelValue', value);
            this.$emit('change', {
                dateRange: value,
                range: range,
                interval: interval,
                startDate: start,
                endDate: end
            });
        }
    }
};
</script>

<style>
.date-range-picker-wrapper {
    display: inline-block;
    min-width: 350px;
    min-height: 40px;
}

/* Ensure Element Plus date picker renders correctly */
.date-range-picker-wrapper .el-date-editor {
    display: inline-flex !important;
    width: 100% !important;
}
</style>
